import { clone } from '../../../webmodule-common/other/clone.js';
import { ContainerManager } from '../../../webmodule-common/other/container-manager.js';
import { DevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid.js';
import { EventSnippet, NullPromise } from '../../../webmodule-common/other/ui/events.js';
import { glossary } from '../../../localization/global.js';
import { localDateToServer, today } from '../../../webmodule-common/other/datetime-converter.js';
import { TermsAndConditions } from '../../api/dealer-api-interface-franchisee.js';
import { TermsAndConditionsApi } from '../../api/terms-and-conditions-api';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store.js';

export class TermsConditionsContainer {
  termsConditionsId: string;
  termsConditions: TermsAndConditions | null;

  constructor(termsConditionId: string, termsCondition: TermsAndConditions | null) {
    this.termsConditionsId = termsConditionId;
    this.termsConditions = termsCondition;
  }
}

export class TermsConditionContainerManager extends ContainerManager<TermsConditionsContainer> {
  protected termsConditionsApi: TermsAndConditionsApi;

  constructor(container: TermsConditionsContainer, title: EventSnippet, termsConditionsApi: TermsAndConditionsApi) {
    super(container, title, glossary('terms-conditions'));
    this.termsConditionsApi = termsConditionsApi;
  }

  /**
   * the id for this managed container
   */
  public get id(): string {
    return this.container.termsConditionsId;
  }

  public get termsConditions(): TermsAndConditions {
    if (!this.container.termsConditions) {
      throw new DevelopmentError(`termsConditions is null`);
    }
    return this.container.termsConditions;
  }

  public async needsTaC() {
    if (!this.container.termsConditions) {
      const result = await this.termsConditionsApi.getTermsAndConditions({
        termsAndConditionsId: this.id
      });
      if (result?.termsAndConditions) {
        this.resetTaC(result.termsAndConditions);
      } else return false;
    }
    return true;
  }

  public resetBackup() {
    this.backup.termsConditions = clone(this.container.termsConditions);
    this.backup.termsConditionsId = this.container.termsConditionsId;
  }

  /**
   * Override this to customize the save logic.
   * @returns True if the save operations have succeeded, false otherwise.
   */
  protected async internalSave(): Promise<boolean> {
    if (this.id == emptyGuid) {
      this.termsConditions.id = newGuid();
      const result = await this.termsConditionsApi.createTermsAndConditions({
        termsAndConditions: this.termsConditions
      });
      if (result?.termsAndConditions) {
        this.resetTaC(result.termsAndConditions);
        return true;
      }
    } else {
      const result = await this.termsConditionsApi.updateTermsAndConditions({
        termsAndConditions: this.termsConditions
      });
      if (result?.termsAndConditions) {
        this.resetTaC(result.termsAndConditions);
        return true;
      }
    }
    return false;
  }

  private resetTaC(taC: TermsAndConditions) {
    this.container.termsConditions = taC;
    this.container.termsConditionsId = this.container.termsConditions.id;
    this.backup.termsConditions = clone(taC);
    this.backup.termsConditionsId = this.backup.termsConditions.id;
  }
}

export async function createNewTermsConditions(): NullPromise<TermsConditionsContainer> {
  const termsAndConditions: TermsAndConditions = {
    id: emptyGuid,
    dateCreated: localDateToServer(today()),
    name: '',
    recordVersion: '',
    supplierId: emptyGuid,
    franchiseeId: userDataStore.franchisee.id,
    branchId: userDataStore.defaultBranch.id,
    terms: '',
    isDefault: false
  };
  return new TermsConditionsContainer(emptyGuid, termsAndConditions);
}
